import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './Header';
import ProductList from './Product/ProductList';
import ProductEditModal from './Product/ProductEditModal';
import StockEditModal from './Product/StockEditModal';
import BulkEditModal from './Product/BulkEditModal';
import BulkStockModal from './Product/BulkStockModal';
import PriceChangeConfirmModal from './Product/PriceChangeConfirmModal';
import CollectionList from './Collection/CollectionList';
import useProducts, { withUpdatedInventory } from '../hooks/useProducts';
import { collectionsAPI, vendorsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

function MainApp() {
  const { showSuccess, showError } = useToast();

  const {
    products,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    updateProduct,
    updateInventory,
    bulkUpdateInventory,
    bulkPercentageChange,
    bulkUpdateProducts
  } = useProducts();

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkStockModalOpen, setBulkStockModalOpen] = useState(false);
  const [percentageValue, setPercentageValue] = useState('');
  const [percentageLoading, setPercentageLoading] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const [priceChangeModalOpen, setPriceChangeModalOpen] = useState(false);
  const [priceChangeType, setPriceChangeType] = useState(null);

  const [catalogView, setCatalogView] = useState('all');
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [activeCollection, setActiveCollection] = useState(null);
  const [collectionProducts, setCollectionProducts] = useState([]);
  const [collectionProductsLoading, setCollectionProductsLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [activeVendor, setActiveVendor] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [vendorProductsLoading, setVendorProductsLoading] = useState(false);

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    try {
      const response = await collectionsAPI.getCollections();
      setCollections(response.data || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Koleksiyonlar yüklenemedi');
    } finally {
      setCollectionsLoading(false);
    }
  }, [showError]);

  const loadCollectionProducts = useCallback(async (collectionId) => {
    setCollectionProductsLoading(true);
    try {
      const response = await collectionsAPI.getCollectionProducts(collectionId);
      setCollectionProducts(response.data || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Koleksiyon ürünleri yüklenemedi');
    } finally {
      setCollectionProductsLoading(false);
    }
  }, [showError]);

  const loadVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const response = await vendorsAPI.getVendors();
      setVendors(response.data || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Satıcılar yüklenemedi');
    } finally {
      setVendorsLoading(false);
    }
  }, [showError]);

  const loadVendorProducts = useCallback(async (vendorName) => {
    setVendorProductsLoading(true);
    try {
      const response = await vendorsAPI.getVendorProducts(vendorName);
      setVendorProducts(response.data || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Satıcı ürünleri yüklenemedi');
    } finally {
      setVendorProductsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (catalogView === 'collections') {
      loadCollections();
    }
    if (catalogView === 'vendors') {
      loadVendors();
    }
  }, [catalogView, loadCollections, loadVendors]);

  const handleCatalogViewChange = (view) => {
    setSelectedIds([]);
    setSearchTerm('');
    setActiveCollection(null);
    setCollectionProducts([]);
    setActiveVendor(null);
    setVendorProducts([]);
    setCatalogView(view);
  };

  const openCollection = async (collection) => {
    setSelectedIds([]);
    setSearchTerm('');
    setActiveCollection(collection);
    setCatalogView('collection');
    await loadCollectionProducts(collection.id);
  };

  const backToCollections = () => {
    setSelectedIds([]);
    setSearchTerm('');
    setActiveCollection(null);
    setCollectionProducts([]);
    setCatalogView('collections');
  };

  const openVendor = async (vendor) => {
    setSelectedIds([]);
    setSearchTerm('');
    setActiveVendor(vendor);
    setCatalogView('vendor');
    await loadVendorProducts(vendor.name || vendor.title);
  };

  const backToVendors = () => {
    setSelectedIds([]);
    setSearchTerm('');
    setActiveVendor(null);
    setVendorProducts([]);
    setCatalogView('vendors');
  };

  const displayedProducts = useMemo(() => {
    const scoped =
      catalogView === 'collection'
        ? collectionProducts
        : catalogView === 'vendor'
          ? vendorProducts
          : null;
    if (!scoped) return products;
    if (!searchTerm.trim()) return scoped;
    const term = searchTerm.toLowerCase();
    return scoped.filter((product) =>
      (product.title || '').toLowerCase().includes(term)
    );
  }, [catalogView, products, collectionProducts, vendorProducts, searchTerm]);

  const visibleCollections = useMemo(() => {
    if (!searchTerm.trim()) return collections;
    const term = searchTerm.toLowerCase();
    return collections.filter((collection) =>
      (collection.title || '').toLowerCase().includes(term)
    );
  }, [collections, searchTerm]);

  const visibleVendors = useMemo(() => {
    if (!searchTerm.trim()) return vendors;
    const term = searchTerm.toLowerCase();
    return vendors.filter((vendor) =>
      (vendor.title || vendor.name || '').toLowerCase().includes(term)
    );
  }, [vendors, searchTerm]);

  const refreshAfterProductChange = async () => {
    if (catalogView === 'collection' && activeCollection) {
      await loadCollectionProducts(activeCollection.id);
    }
    if (catalogView === 'vendor' && activeVendor) {
      await loadVendorProducts(activeVendor.name || activeVendor.title);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openPriceChangeModal = (type) => {
    const value = parseFloat(percentageValue);
    if (isNaN(value) || value <= 0) {
      showError('Lütfen geçerli bir yüzde değeri girin!');
      return;
    }

    if (selectedIds.length === 0) {
      showError('Lütfen en az bir ürün seçin!');
      return;
    }

    setPriceChangeType(type);
    setPriceChangeModalOpen(true);
  };

  const closePriceChangeModal = () => {
    setPriceChangeModalOpen(false);
    setPriceChangeType(null);
  };

  const handlePercentageChange = async () => {
    if (!priceChangeType) return;

    const value = parseFloat(percentageValue);
    setPercentageLoading(priceChangeType);
    closePriceChangeModal();

    try {
      const result = await bulkPercentageChange(selectedIds, value, priceChangeType);

      if (result.success) {
        const actionText = priceChangeType === 'increase' ? 'artırıldı' : 'azaltıldı';
        showSuccess(`${result.data.updatedCount} ürünün fiyatı %${value} ${actionText}!`);
        setSelectedIds([]);
        setPercentageValue('');
        await refreshAfterProductChange();
      } else {
        showError('Güncelleme sırasında hata oluştu: ' + result.error);
      }
    } catch (err) {
      showError('Beklenmeyen bir hata oluştu!');
    } finally {
      setPercentageLoading(null);
    }
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
  };

  const closeEditModal = () => {
    setSelectedProduct(null);
  };

  const openStockModal = (product) => {
    setStockProduct(product);
  };

  const closeStockModal = () => {
    setStockProduct(null);
  };

  const handleSaveStock = async (quantity) => {
    if (!stockProduct) return;
    setEditLoading(true);

    try {
      const result = await updateInventory(stockProduct.id, quantity);

      if (result.success) {
        const savedQty = result.data?.quantity ?? quantity;
        const patchQty = (prev) =>
          prev.map((product) =>
            product.id === stockProduct.id
              ? withUpdatedInventory(product, savedQty)
              : product
          );
        setCollectionProducts(patchQty);
        setVendorProducts(patchQty);
        showSuccess('Stok güncellendi!');
        closeStockModal();
      } else {
        showError(result.error || 'Stok güncellenemedi');
      }
    } catch (err) {
      showError('Beklenmeyen bir hata oluştu!');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveProduct = async (productData) => {
    setEditLoading(true);

    try {
      const result = await updateProduct(selectedProduct.id, productData);

      if (result.success) {
        showSuccess('Ürün başarıyla güncellendi!');
        closeEditModal();
        await refreshAfterProductChange();
      } else {
        showError('Güncelleme sırasında hata oluştu: ' + result.error);
      }
    } catch (err) {
      showError('Beklenmeyen bir hata oluştu!');
    } finally {
      setEditLoading(false);
    }
  };

  const openBulkModal = () => {
    setBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
  };

  const applyCollectionInventories = (updates) => {
    if (!updates?.length) return;
    const byId = new Map(updates.map((u) => [u.productId ?? u.id, u.quantity]));
    const patch = (prev) =>
      prev.map((product) =>
        byId.has(product.id)
          ? withUpdatedInventory(product, byId.get(product.id))
          : product
      );
    setCollectionProducts(patch);
    setVendorProducts(patch);
  };

  const openBulkStockModal = () => {
    if (selectedIds.length === 0) {
      showError('Lütfen en az bir ürün seçin!');
      return;
    }
    setBulkStockModalOpen(true);
  };

  const closeBulkStockModal = () => {
    setBulkStockModalOpen(false);
  };

  const handleBulkStockSave = async (items) => {
    setEditLoading(true);

    try {
      const result = await bulkUpdateInventory(items);

      if (result.success || result.data?.updatedCount > 0) {
        applyCollectionInventories(result.data?.results?.length ? result.data.results : items);
        const count = result.data?.updatedCount ?? items.length;
        showSuccess(`${count} ürünün stoğu güncellendi!`);
        if (result.data?.errors?.length) {
          showError(result.data.errors.slice(0, 3).join(' '));
        }
        setSelectedIds([]);
        closeBulkStockModal();
      } else {
        showError(result.error || 'Toplu stok güncellenemedi');
      }
    } catch (err) {
      showError('Beklenmeyen bir hata oluştu!');
    } finally {
      setEditLoading(false);
    }
  };

  const handleBulkSave = async (productsToUpdate) => {
    setEditLoading(true);

    try {
      const result = await bulkUpdateProducts(productsToUpdate);

      if (result.success) {
        showSuccess(`${result.data.updatedCount} ürün başarıyla güncellendi!`);
        setSelectedIds([]);
        closeBulkModal();
        await refreshAfterProductChange();
      } else {
        showError('Güncelleme sırasında hata oluştu: ' + result.error);
      }
    } catch (err) {
      showError('Beklenmeyen bir hata oluştu!');
    } finally {
      setEditLoading(false);
    }
  };

  const selectedProducts = selectedIds
    .map((id) => displayedProducts.find((product) => product.id === id))
    .filter(Boolean);

  if (error && catalogView === 'all') {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>🚨 Hata Oluştu</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        percentageValue={percentageValue}
        onPercentageChange={setPercentageValue}
        onPercentageIncrease={() => openPriceChangeModal('increase')}
        onPercentageDecrease={() => openPriceChangeModal('decrease')}
        onBulkEdit={openBulkModal}
        onBulkStock={openBulkStockModal}
        selectedCount={selectedIds.length}
        percentageLoading={percentageLoading}
        products={displayedProducts}
        catalogView={catalogView}
        onCatalogViewChange={handleCatalogViewChange}
        activeCollection={activeCollection}
        onBackToCollections={backToCollections}
        collectionCount={visibleCollections.length}
        activeVendor={activeVendor}
        onBackToVendors={backToVendors}
        vendorCount={visibleVendors.length}
      />

      <main className="main-content">
        {catalogView === 'collections' ? (
          <CollectionList
            collections={visibleCollections}
            onOpenCollection={openCollection}
            loading={collectionsLoading}
          />
        ) : catalogView === 'vendors' ? (
          <CollectionList
            collections={visibleVendors}
            onOpenCollection={openVendor}
            loading={vendorsLoading}
            labels={{
              loading: 'Satıcılar yükleniyor...',
              emptyTitle: 'Satıcı bulunamadı',
              emptyHint: 'Shopify’da satıcı (vendor) atanmış ürün yok.',
              columnTitle: 'Satıcı',
              emptyIcon: '🏷️',
              rowIcon: '🏷️'
            }}
          />
        ) : (
          <ProductList
            products={displayedProducts}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSetSelectedIds={setSelectedIds}
            onEditProduct={openEditModal}
            onEditStock={openStockModal}
            loading={
              catalogView === 'collection'
                ? collectionProductsLoading
                : catalogView === 'vendor'
                  ? vendorProductsLoading
                  : loading
            }
          />
        )}
      </main>

      <ProductEditModal
        isOpen={!!selectedProduct}
        onClose={closeEditModal}
        product={selectedProduct}
        onSave={handleSaveProduct}
        loading={editLoading}
      />

      <StockEditModal
        isOpen={!!stockProduct}
        onClose={closeStockModal}
        product={stockProduct}
        onSave={handleSaveStock}
        loading={editLoading}
      />

      <BulkEditModal
        isOpen={bulkModalOpen}
        onClose={closeBulkModal}
        selectedProducts={selectedProducts}
        onSave={handleBulkSave}
        loading={editLoading}
      />

      <BulkStockModal
        isOpen={bulkStockModalOpen}
        onClose={closeBulkStockModal}
        selectedProducts={selectedProducts}
        onSave={handleBulkStockSave}
        loading={editLoading}
      />

      {selectedProducts.length > 0 && (
        <PriceChangeConfirmModal
          isOpen={priceChangeModalOpen}
          onClose={closePriceChangeModal}
          onConfirm={handlePercentageChange}
          loading={percentageLoading}
          type={priceChangeType}
          percentage={parseFloat(percentageValue)}
          sampleProduct={selectedProducts[0]}
        />
      )}
    </div>
  );
}

export default MainApp;
